import Vue from 'vue';
import Router from 'vue-router';
import Home from './views/Home';
import Login from './views/Login';
import ResetPassword from './views/ResetPassword';
import UserService from './services/user.service';
import GameService from './services/game/game.service';
import store from './store';
import * as _ from 'lodash';

Vue.use(Router);

export const router = new Router({
  mode: 'history',
  routes: [
    {
      path: '/login',
      component: Login,
    },
    {
      // :token must be the reset UUID from the forgot-password email (findByResetToken). Not JWT / not Bearer / not localStorage accessToken.
      path: '/reset-password/:token',
      component: ResetPassword,
    },
    {
      path: '/embed-chart/:chartId/key/:key/compId/:componentId/catId/:categoryId/tId/:toolId',
      component: () => import('./views/base/EmbedChart.vue'),
      name: 'embed-chart'
    },
    {
      path: '/ext/:shareToken',
      component: () => import('./views/external/ExternalPages.vue'),
      children:[
        {
          path: '',
          name: 'external-share',
          component: () => import('./views/external/ExternalHome.vue')
        },
        {
          path: "video-highlights/playlist/:id",
          name: 'external-video-playlist',
          component: () => import('./views/video-highlights/VideoHighlightsView')
        }
      ]
    },
    {
      path:'/vision',
      component: () => import('./views/administration/VisionView.vue'),
      name: 'vision-tool'
    },
    {
      path: '/',
      component: () => import('./views/Pages'),
      meta: {
        authorize: 'ROLE_USER',
      },
      children: [
        {
          path: '/',
          redirect: '/home',
          component: () => import('./views/TopHeader'),
          children: [
            {
              path: '/',
              redirect: 'home',
            },
            {
              path: '/home',
              name: 'home',
              component: Home,
              meta: {
                authorize: 'ROLE_USER',
                pageIcon: 'home',
                pageTitle: 'dashboards.title',
                pageSubtitle: 'dashboards.subtitle'
              },
            },
            {
              path: '/settings',
              name: 'settings',
              // lazy-loaded
              component: () => import('./views/Settings.vue'),
              meta: {
                authorize: 'ROLE_USER',
                pageIcon: 'settings',
                pageTitle: 'general.settings',
                pageSubtitle: 'settings.pageSubtitle'
              },
            },
            {
              path: '/profile',
              name: 'userProfile',
              component: () => import('./views/UserProfile.vue'),
              meta: {
                authorize: 'ROLE_USER',
                pageIcon: 'person',
                pageTitle: 'profile.title',
                pageSubtitle: 'profile.subtitle'
              },
            },
            {
              path: '/administration',
              name: 'administration',
              component: () => import('./views/Administration'),
              meta: {
                authorize: 'ROLE_ADMIN',
                pageIcon: 'apps',
                pageTitle: 'app.administration',
                pageSubtitle: 'administration.pageSubtitle'
              },
            },
            {
              path: '/media',
              name: 'administration-media',
              component: () => import('./views/MediaAdministration'),
              meta: {
                authorize: 'ROLE_USER',
                pageIcon: 'perm_media',
                pageTitle: 'mediaMode.title',
                pageSubtitle: 'mediaMode.pageSubtitle'
              }
            },
            {
              path: '/similarity',
              name: 'similarity',
              component: () => import('./views/Similarity'),
              meta: {
                authorize: 'ROLE_EVENTING_DATA',
                pageIcon: 'blur_circular',
                pageTitle: 'general.similarity',
                pageSubtitle: 'similarity.subtitle'
              }
            },
            {
              path: '/player-scout',
              name: 'player-scout',
              component: () => import('./views/player/PlayerScout'),
              meta: {
                authorize: ['ROLE_EVENTING_DATA', 'ROLE_PHYSICAL_DATA'],
                pageIcon: 'center_focus_strong',
                pageTitle: 'app.playerScout',
                pageSubtitle: 'playerScout.dlabDatabase'
              },
            },
            {
              path: '/player-scout/:id',
              name: 'player-scout-saved',
              component: () => import('./views/player/PlayerScout'),
              meta: {
                authorize: ['ROLE_EVENTING_DATA', 'ROLE_PHYSICAL_DATA'],
              },
            },
            {
              path: '/player-alerts',
              name: 'player-alerts',
              component: () => import('./views/player/PlayerAlerts'),
              meta: {
                authorize: ['ROLE_DRIBLAB_HUB', 'ROLE_PHYSICAL_DATA', 'ROLE_EVENTING_DATA'],
              },
            },
            {
              path: '/player-alert',
              name: 'player-alert-detail',
              component: () => import('./views/player/PlayerAlertsDetail'),
              meta: {
                authorize: ['ROLE_DRIBLAB_HUB', 'ROLE_PHYSICAL_DATA', 'ROLE_EVENTING_DATA'],
                pageIcon: 'notifications_on',
                pageTitle: 'app.playerAlerts',
                pageSubtitle: 'alerts.subtitlePlayer'
              },
            },
            {
              path: '/alerts',
              name: 'alerts',
              component: () => import('./views/alerts/AlertsView'),
              meta: {
                authorize: ['ROLE_DRIBLAB_HUB', 'ROLE_PHYSICAL_DATA', 'ROLE_EVENTING_DATA'],
                pageIcon: 'notifications_on',
                pageTitle: 'general.alerts',
                pageSubtitle: 'alerts.generalSubtitle'
              },
            },
            {
              path: '/search-alerts',
              name: 'search-alerts',
              component: () => import('./views/player/SearchAlerts'),
              meta: {
                authorize: ['ROLE_DRIBLAB_HUB', 'ROLE_EVENTING_DATA'],
              },
            },
            {
              path: '/player-comparison',
              name: 'player-comparison',
              component: () => import('./views/player/PlayerComparison'),
              meta: {
                authorize: 'ROLE_EVENTING_DATA',
                pageIcon: 'compare',
                pageTitle: 'app.playerComparison',
                pageSubtitle: 'playerComparison.upTo5Players'
              },
            },
            {
              path: '/user/:id',
              name: 'user-stats',
              component: () => import('./views/dashboard/UserDashboard'),
              meta: {
                authorize: 'ROLE_ADMIN',
                pageIcon: 'person',
                pageTitle: 'dashboards.userDashboard',
                pageSubtitle: 'dashboards.userDashboardSubtitle'
              },
            },
            {
              path: '/player-comparison/:ids',
              name: 'player-comparison-ids',
              component: () => import('./views/player/PlayerComparison'),
              meta: {
                authorize: 'ROLE_EVENTING_DATA',
                pageIcon: 'compare',
                pageTitle: 'app.playerComparison',
                pageSubtitle: 'playerComparison.upTo5Players'
              },
            },
            {
              path: '/favorites',
              name: 'favorites',
              component: () => import('./views/Favorites'),
              meta: {
                authorize: 'ROLE_USER',
                pageIcon: 'star',
                pageTitle: 'general.favorites',
                pageSubtitle: 'favorites.subtitle'
              },
            },
            {
              path: '/team-favorites',
              redirect: '/favorites?tab=teams'
            },
            {
              path: '/inbox',
              name: 'inbox',
              component: () => import('./views/inbox/Inbox'),
              meta: {
                authorize: 'ROLE_USER',
                pageIcon: 'all_inbox',
                pageTitle: 'app.inbox',
                pageSubtitle: 'inbox.subtitle'
              },
            },
            {
              path: '/collections',
              name: 'collections',
              component: () => import('./views/collections/Collections'),
              meta: {
                authorize: 'ROLE_USER',
                pageIcon: 'people',
                pageTitle: 'collections.title',
                pageSubtitle: 'collections.subtitle'
              },
            },
            {
              path: '/collections/:id',
              name: 'collection-detail',
              component: () => import('./views/collections/CollectionDetail'),
              meta: {
                authorize: 'ROLE_USER',
                pageIcon: 'people',
                pageTitle: 'collections.title',
                pageSubtitle: 'collections.subtitle'
              },
            },
            {
              path: '/search',
              name: 'search',
              component: () => import('./views/GlobalSearch'),
              meta: {
                authorize: 'ROLE_USER',
                pageIcon: 'location_searching',
                pageTitle: 'general.globalSearch',
                pageSubtitle: 'globalSearch.subtitle'
              },
            },
            {
              path: '/game-analysis',
              name: 'game-analysis',
              component: () => import('./views/game/GameCalendar'),
              meta: {
                authorize: 'ROLE_USER',
                pageIcon: 'insert_chart_outlined',
                pageTitle: 'app.gameAnalysis',
                pageSubtitle: 'gameAnalysis.subtitle3'
              },
            },
            {
              path: '/custom/radar',
              name: 'custom-radar',
              component: () => import('./views/custom-templates/CustomRadarTemplate'),
              meta: {
                authorize: 'ROLE_USER',
                pageIcon: 'insert_chart_outlined',
                pageTitle: 'settings.radarTemplates',
                pageSubtitle: 'settings.radarTemplatesSubtitle'
              },
            },
            {
              path: '/custom/season-list',
              name: 'custom-season-list',
              component: () => import('./views/custom-templates/CustomSeasonsTemplate'),
              meta: {
                authorize: 'ROLE_USER',
                pageIcon: 'insert_chart_outlined',
                pageTitle: 'settings.seasonTemplates',
                pageSubtitle: 'settings.seasonTemplatesSubtitle'
              },
            },
            {
              path: '/custom/stats',
              name: 'custom-stats',
              component: () => import('./views/custom-templates/CustomStatsTemplate'),
              meta: {
                authorize: 'ROLE_USER',
                pageIcon: 'insert_chart_outlined',
                pageTitle: 'settings.statTemplates',
                pageSubtitle: 'settings.statTemplatesSubtitle'
              },
            },
            {
              path: '/scouting-map',
              name: 'scouting-map',
              component: () => import('./views/game/GameMap'),
              meta: {
                authorize: 'ROLE_DRIBLAB_HUB',
                pageIcon: 'map',
                pageTitle: 'Scouting Map',
                pageSubtitle: 'gameMap.subtitle'
              },
            },
            {
              path: '/tasks-board',
              name: 'tasks-board',
              component: () => import('./views/scouting-tasks-board/TasksBoardView'),
              meta: {
                authorize: 'ROLE_DRIBLAB_HUB',
                pageIcon: 'assignment_turned_in',
                pageTitle: 'Tasks Board',
                pageSubtitle: 'scoutingCalendar.subtitle'
              },
            },
            {
              path: '/scouting-calendar',
              name: 'scouting-calendar',
              component: () => import('./views/scouting-calendar/ScoutingCalendarView'),
              meta: {
                authorize: 'ROLE_DRIBLAB_HUB',
                pageIcon: 'event',
                pageTitle: 'Scouting Calendar',
                pageSubtitle: 'scoutingCalendar.subtitle'
              },
            },
            {
              path: '/video-teams',
              name: 'video-teams',
              component: () => import('./views/video-teams/VideoTeamsView'),
              meta: {
                authorize: 'ROLE_VIDEO',
                pageIcon: 'dvr',
                pageTitle: 'searchbar.teams',
                pageSubtitle: 'videoTeams.subtitle'
              },
            },
            {
              path: '/video-highlights',
              name: 'video-highlights',
              component: () => import('./views/video-highlights/VideoHighlightsView'),
              meta: {
                authorize: 'ROLE_VIDEO',
                pageIcon: 'ondemand_video',
                pageTitle: 'game.header.video',
                pageSubtitle: 'videoEvents.subtitle'
              },
            },
            {
              path: '/video-highlights/playlist/:id',
              name: 'video-highlights-playlist',
              component: () => import('./views/video-highlights/VideoHighlightsView'),
              meta: {
                authorize: 'ROLE_VIDEO',
                pageIcon: 'ondemand_video',
                pageTitle: 'game.header.video',
                pageSubtitle: 'videoEvents.subtitle'
              },
            },
            {
              path: '/video-highlights/player/:id',
              name: 'video-highlights-player',
              component: () => import('./views/video-highlights/VideoHighlightsView'),
              meta: {
                authorize: 'ROLE_VIDEO',
                pageIcon: 'ondemand_video',
                pageTitle: 'game.header.video',
                pageSubtitle: 'videoEvents.subtitle'
              },
            },
            {
              path: '/video-highlights/team/:id',
              name: 'video-highlights-team',
              component: () => import('./views/video-highlights/VideoHighlightsView'),
              meta: {
                authorize: 'ROLE_VIDEO',
                pageIcon: 'ondemand_video',
                pageTitle: 'game.header.video',
                pageSubtitle: 'videoEvents.subtitle'
              },
            },
            {
              path: '/video-highlights/game/:id',
              name: 'video-highlights-game',
              component: () => import('./views/video-highlights/VideoHighlightsView'),
              meta: {
                authorize: 'ROLE_VIDEO',
                pageIcon: 'ondemand_video',
                pageTitle: 'game.header.video',
                pageSubtitle: 'videoEvents.subtitle'
              },
            },
            {
              path: '/video-highlights/game/:id/full-game',
              name: 'video-highlights-full-game',
              component: () => import('./views/video-highlights/VideoHighlightsView'),
              meta: {
                authorize: 'ROLE_VIDEO',
                pageIcon: 'ondemand_video',
                pageTitle: 'game.header.video',
                pageSubtitle: 'videoEvents.subtitle'
              },
            },
            {
              path: '/video-highlights/game/:id/recap',
              name: 'video-highlights-game-recap',
              component: () => import('./views/video-highlights/VideoHighlightsView'),
              meta: {
                authorize: 'ROLE_VIDEO',
                pageIcon: 'ondemand_video',
                pageTitle: 'game.header.video',
                pageSubtitle: 'videoEvents.subtitle'
              },
            },
            {
              path: '/video-highlights/game/:id/tId/:teamId/stat/:statId',
              name: 'video-highlights-game-team-stat',
              component: () => import('./views/video-highlights/VideoHighlightsView'),
              meta: {
                authorize: 'ROLE_VIDEO',
                pageIcon: 'ondemand_video',
                pageTitle: 'game.header.video',
                pageSubtitle: 'videoEvents.subtitle'
              },
            },
            {
              path: '/video-highlights/game/:id/pId/:playerId/stat/:statId',
              name: 'video-highlights-game-player-stat',
              component: () => import('./views/video-highlights/VideoHighlightsView'),
              meta: {
                authorize: 'ROLE_VIDEO',
                pageIcon: 'ondemand_video',
                pageTitle: 'game.header.video',
                pageSubtitle: 'videoEvents.subtitle'
              },
            },
            {
              path: '/video-upload',
              name: 'video-upload',
              component: () => import('./views/video-upload/VideoUploadView'),
              meta: {
                authorize: 'ROLE_VIDEO',
                pageIcon: 'cloud_upload',
                pageTitle: 'app.videoUploadTool',
                pageSubtitle: 'videoUpload.subtitle'
              },
            },
            {
              path: '/uploaded-games',
              name: 'uploaded-games',
              component: () => import('./views/uploaded-games/UploadedGamesView'),
              meta: {
                authorize: 'ROLE_VIDEO',
                pageIcon: 'ballot',
                pageTitle: 'app.uploadedGames',
                pageSubtitle: 'uploadedGames.subtitle'
              },
            },
            {
              path: '/video-playlists',
              name: 'video-playlists',
              component: () => import('./views/video-playlists/VideoPlaylistsView'),
              meta: {
                authorize: 'ROLE_VIDEO',
                pageIcon: 'art_track',
                pageTitle: 'app.videoPlaylists',
                pageSubtitle: 'videoPlaylists.subtitle'

              },
            },
            {
              path: '/arrigo-insights',
              name: "arrigoInsights",
              component: () => import('./views/arrigo/insights/ArrigoInsightsView.vue'),
              meta: {
                authorize: 'ROLE_ARRIGO',
                pageIcon: 'emoji_events',
                pageTitle: 'arrigo.insights',
                pageSubtitle: 'arrigo.insightsSubtitle'
              }
            },
            {
              path: '/dynamic-tracking',
              name: 'dynamic-tracking',
              component: () => import('./views/arrigo/ArrigoView'),
              meta: {
                authorize: 'ROLE_ARRIGO',
                pageIcon: 'fa-code-compare',
                pageTitle: 'app.arrigo',
                pageSubtitle: 'arrigo.subtitle'
              },
            },
            {
              path: '/spatial-coverage',
              name: 'spatial-coverage',
              component: () => import('./views/arrigo/SpatialCoverageView'),
              meta: {
                authorize: 'ROLE_ARRIGO',
                pageIcon: 'fa-circle-nodes',
                pageTitle: 'faq.metrics.categories.SpatialCoverage',
                pageSubtitle: 'arrigo.subtitle'
              },
            },
            {
              path: '/phases-of-game',
              name: 'phases-of-game',
              component: () => import('./views/arrigo/PhaseOfPlayView'),
              meta: {
                authorize: 'ROLE_ARRIGO',
                pageIcon: 'fa-up-down',
                pageTitle: 'team.trendsTab.phasesOfGame',
                pageSubtitle: 'arrigo.subtitle'
              },
            },
            {
              path: '/line-breaking-passes',
              name: 'line-breaking-passes',
              component: () => import('./views/arrigo/LBPEventsView'),
              meta: {
                authorize: 'ROLE_ARRIGO',
                pageIcon: 'fa-i-cursor',
                pageTitle: 'statsNaming.LineBreakingPasses',
                pageSubtitle: 'arrigo.lineBreakingPassesSubtitle'
              },
            },
            {
              path: '/passes-under-pressure',
              name: 'passes-under-pressure',
              component: () => import('./views/arrigo/PassesUnderPressure.vue'),
              meta: {
                authorize: 'ROLE_ARRIGO',
                pageIcon: 'fa-crosshairs',
                pageTitle: 'faq.metrics.categories.PassesUnderPressure',
                pageSubtitle: 'arrigo.passesUnderPressureSubtitle'
              },
            },
            {
              path: '/on-ball-pressure',
              name: 'on-ball-pressure',
              component: () => import('./views/arrigo/OnBallPressureView'),
              meta: {
                authorize: 'ROLE_ARRIGO',
                pageIcon: 'fa-hand-paper',
                pageTitle: 'faq.metrics.categories.OnBallPressure',
                pageSubtitle: 'arrigo.onBallPressureSubtitle'
              },
            },
            {
              path: '/ball-receipts-in-space',
              name: 'ball-receipts-in-space',
              component: () => import('./views/arrigo/BallReceiptsInSpaceView'),
              meta: {
                authorize: 'ROLE_ARRIGO',
                pageIcon: 'fa-futbol',
                pageTitle: 'faq.metrics.categories.BallReceiptsInSpace',
                pageSubtitle: 'arrigo.ballReceiptsInSpaceSubtitle'
              },
            },
            {
              path: '/off-ball-run',
              name: 'off-ball-run',
              component: () => import('./views/arrigo/OBREventsView'),
              meta: {
                authorize: 'ROLE_ARRIGO',
                pageIcon: 'fa-hand-scissors',
                pageTitle: 'faq.metrics.categories.OffBallRuns',
                pageSubtitle: 'arrigo.offBallRunSubtitle'
              },
            },
            {
              path: '/report/:id',
              name: 'report-detail',
              component: () => import('./views/inbox/ReportDetail'),
              meta: {
                authorize: 'ROLE_USER',
                pageIcon: 'assignment',
                pageTitle: 'requestReport.detailTitle',
                pageSubtitle: 'requestReport.detailSubtitle'
              },
            },
            {
              path: '/player/:id',
              component: () => import('./views/player/PlayerHeader'),
              children: [
                {
                  path: '',
                  name: 'player',
                  component: () => import('./views/player/PlayerRecruitment'),
                  meta: {
                    authorize: 'ROLE_USER',
                    pageIcon: 'directions_run',
                    pageTitle: 'playerRecruitment.title',
                    pageSubtitle: 'playerRecruitment.subtitle'
                  }
                },
                {
                  path: '/player/:id/performance',
                  name: 'player-performance',
                  component: () => import('./views/player/PlayerRecruitment'),
                  meta: {
                    authorize: ['ROLE_EVENTING_DATA', 'ROLE_PHYSICAL_DATA'],
                    pageIcon: 'directions_run',
                    pageTitle: 'playerRecruitment.title',
                    pageSubtitle: 'playerRecruitment.subtitle'
                  }
                },
                {
                  path: '/player/:id/video-highlights',
                  name: 'player-video-highlights',
                  component: () => import('./views/video-highlights/VideoHighlightsView'),
                  meta: {
                    authorize: ['ROLE_EVENTING_DATA', 'ROLE_PHYSICAL_DATA'],
                    pageIcon: 'directions_run',
                    pageTitle: 'playerRecruitment.title',
                    pageSubtitle: 'playerRecruitment.subtitle'
                  }
                },
                {
                  path: '/player/:id/info',
                  name: 'player-info',
                  component: () => import('./views/player/PlayerInfo'),
                  meta: {
                    authorize: 'ROLE_USER',
                    pageIcon: 'directions_run',
                    pageTitle: 'playerRecruitment.title',
                    pageSubtitle: 'playerRecruitment.subtitle'
                  },
                  children: [
                    {
                      path: '/player/:id/info/injuries',
                      name: 'player-info-injuries',
                      component: () => import('./views/player/PlayerInfo'),
                      meta: {
                        authorize: 'ROLE_USER',
                        pageIcon: 'directions_run',
                        pageTitle: 'playerRecruitment.title',
                        pageSubtitle: 'playerRecruitment.subtitle'
                      },
                    },
                  ],
                },
                {
                  path: '/player/:id/maps',
                  name: 'player-maps',
                  component: () => import('./views/player/PlayerMaps'),
                  meta: {
                    authorize: 'ROLE_EVENTING_DATA',
                    pageIcon: 'directions_run',
                    pageTitle: 'playerRecruitment.title',
                    pageSubtitle: 'playerRecruitment.subtitle'
                  }
                },
                {
                  path: '/player/:id/arrigo',
                  name: 'player-arrigo-maps',
                  component: () => import('./views/player/PlayerArrigoMaps'),
                  meta: {
                    authorize: 'ROLE_ARRIGO',
                    pageIcon: 'directions_run',
                    pageTitle: 'playerRecruitment.title',
                    pageSubtitle: 'playerRecruitment.subtitle'
                  }
                },
                {
                  path: '/player/:id/beeswarm',
                  name: 'player-beeswarm',
                  component: () => import('./views/player/PlayerBeeswarmView'),
                  meta: {
                    authorize: ['ROLE_EVENTING_DATA', 'ROLE_PHYSICAL_DATA'],
                    pageIcon: 'directions_run',
                    pageTitle: 'playerRecruitment.title',
                    pageSubtitle: 'playerRecruitment.subtitle'
                  }
                },
                {
                  path: '/player/:id/all-games',
                  name: 'player-all-games',
                  component: () => import('./views/player/PlayerAllGames'),
                  meta: {
                    authorize: 'ROLE_USER',
                    pageIcon: 'directions_run',
                    pageTitle: 'playerRecruitment.title',
                    pageSubtitle: 'playerRecruitment.subtitle'
                  }
                },
                {
                  path: '/player/:id/gbe',
                  name: 'player-gbe',
                  component: () => import('./views/player/PlayerGbe'),
                  meta: {
                    authorize: 'ROLE_EVENTING_DATA',
                    pageIcon: 'directions_run',
                    pageTitle: 'playerRecruitment.title',
                    pageSubtitle: 'playerRecruitment.subtitle'
                  }
                },
                {
                  path: '/player/:id/scouting-analysis',
                  name: 'scouting-analysis',
                  component: () => import('./views/scouting-analysis/PlayerScoutingAnalysisView'),
                  meta: {
                    authorize: ['ROLE_DRIBLAB_HUB', 'ROLE_PHYSICAL_DATA', 'ROLE_EVENTING_DATA'],
                    pageIcon: 'directions_run',
                    pageTitle: 'playerRecruitment.title',
                    pageSubtitle: 'playerRecruitment.subtitle'
                  }
                },
                {
                  path: '/player/:id/inactive',
                  name: 'player-inactive',
                  component: () => import('./views/player/PlayerInactive'),
                  meta: {
                    authorize: 'ROLE_USER',
                    pageIcon: 'directions_run',
                    pageTitle: 'playerRecruitment.title',
                    pageSubtitle: 'playerRecruitment.subtitle'
                  }
                },
                {
                  path: '/player/:id/physical-progression',
                  name: 'player-physical-progression',
                  component: () => import('./views/player/PlayerPhysicalProgressionView'),
                  meta: {
                    authorize: ['ROLE_PHYSICAL_DATA'],
                    pageIcon: 'directions_run',
                    pageTitle: 'playerRecruitment.title',
                    pageSubtitle: 'playerRecruitment.subtitle'
                  }
                },
                {
                  path: '/player/:id/stats',
                  name: 'player-stats',
                  component: () => import('./views/player/PlayerStats'),
                  meta: {
                    authorize: ['ROLE_EVENTING_DATA'],
                    pageIcon: 'directions_run',
                    pageTitle: 'playerRecruitment.title',
                    pageSubtitle: 'playerRecruitment.subtitle'
                  }
                },
              ],
              meta: {
                authorize: 'ROLE_USER',
                pageIcon: 'directions_run',
                pageTitle: 'playerRecruitment.title',
                pageSubtitle: 'playerRecruitment.subtitle'
              }
            },
            {
              path: '/game/:id',
              component: () => import('./views/game/GameHeader'),
              children: [
                {
                  path: '',
                  name: 'game',
                  component: () => import('./views/game/Game'),
                  meta: {
                    authorize: 'ROLE_USER',
                    pageIcon: 'sports_soccer',
                    pageTitle: 'game.title',
                    pageSubtitle: 'game.subtitle'
                  }
                },
                {
                  path: '/game/:id/details',
                  name: 'game-details',
                  component: () => import('./views/game/Game'),
                  meta: {
                    authorize: 'ROLE_USER',
                    pageIcon: 'sports_soccer',
                    pageTitle: 'game.title',
                    pageSubtitle: 'game.subtitle'
                  }
                },
                {
                  path: '/game/:id/video-highlights',
                  name: 'game-video-highlights',
                  component: () => import('./views/video-highlights/VideoHighlightsView'),
                  meta: {
                    authorize: 'ROLE_USER',
                    pageIcon: 'sports_soccer',
                    pageTitle: 'game.title',
                    pageSubtitle: 'game.subtitle'
                  }
                },
                {
                  path: '/game/:id/video-highlights/pId/:playerId/stat/:statId',
                  name: 'game-player-stat-video-highlights',
                  component: () => import('./views/video-highlights/VideoHighlightsView'),
                  meta: {
                    authorize: 'ROLE_VIDEO',
                    pageIcon: 'ondemand_video',
                    pageTitle: 'game.header.video',
                    pageSubtitle: 'videoEvents.subtitle'
                  },
                },
                {
                  path: '/game/:id/video-highlights/:homeAwayModel/stat/:statId',
                  name: 'game-stat-video-highlights',
                  component: () => import('./views/video-highlights/VideoHighlightsView'),
                  meta: {
                    authorize: 'ROLE_VIDEO',
                    pageIcon: 'ondemand_video',
                    pageTitle: 'game.header.video',
                    pageSubtitle: 'videoEvents.subtitle'
                  },
                },
                {
                  path: '/game/:id/stats',
                  name: 'game-stats',
                  component: () => import('./views/game/GameStats'),
                  meta: {
                    authorize: 'ROLE_EVENTING_DATA',
                    pageIcon: 'sports_soccer',
                    pageTitle: 'game.title',
                    pageSubtitle: 'game.subtitle'
                  }
                },
                {
                  path: '/game/:id/physical-stats',
                  name: 'game-physical-stats',
                  component: () => import('./views/game/GamePhysicalStats'),
                  meta: {
                    authorize: ['ROLE_ADMIN', 'ROLE_PHYSICAL_DATA'],
                    pageIcon: 'sports_soccer',
                    pageTitle: 'game.title',
                    pageSubtitle: 'game.subtitle'
                  }
                },
                {
                  path: '/game/:id/video',
                  name: 'game-video',
                  component: () => import('./views/game/GameVideo'),
                  meta: {
                    authorize: 'ROLE_VIDEO',
                    pageIcon: 'sports_soccer',
                    pageTitle: 'game.title',
                    pageSubtitle: 'game.subtitle'
                  }
                },
                {
                  path: '/game/:id/maps',
                  name: 'game-maps',
                  component: () => import('./views/game/GameMaps'),
                  meta: {
                    authorize: 'ROLE_EVENTING_DATA',
                    pageIcon: 'sports_soccer',
                    pageTitle: 'game.title',
                    pageSubtitle: 'game.subtitle'
                  },
                },
                {
                  path: '/game/:id/season/:seasonId/arrigo',
                  name: 'game-arrigo-maps',
                  component: () => import('./views/game/GameArrigoMaps'),
                  meta: {
                    authorize: 'ROLE_ARRIGO',
                    pageIcon: 'sports_soccer',
                    pageTitle: 'game.title',
                    pageSubtitle: 'game.subtitle'
                  },
                },
                {
                  path: '/game/:id/game-flow',
                  name: 'game-flow',
                  component: () => import('./views/game/GameFlowView'),
                  meta: {
                    authorize: 'ROLE_EVENTING_DATA',
                    pageIcon: 'sports_soccer',
                    pageTitle: 'game.title',
                    pageSubtitle: 'game.subtitle'
                  }
                },
                {
                  path: '/game/:id/post-game-analysis',
                  name: 'post-game-analysis',
                  component: () => import('./views/game/PostGameAnalysis'),
                  meta: {
                    authorize: 'ROLE_EVENTING_DATA',
                    pageIcon: 'sports_soccer',
                    pageTitle: 'game.title',
                    pageSubtitle: 'game.subtitle'
                  }
                },
                {
                  path: '/game/:id/scouting-analysis',
                  name: 'documents',
                  component: () => import('./views/scouting-analysis/GameScoutingAnalysisView'),
                  meta: {
                    authorize: ['ROLE_DRIBLAB_HUB', 'ROLE_PHYSICAL_DATA', 'ROLE_EVENTING_DATA'],
                    pageIcon: 'sports_soccer',
                    pageTitle: 'game.title',
                    pageSubtitle: 'game.subtitle'
                  }
                }
              ],
              meta: {
                authorize: 'ROLE_USER',
                pageIcon: 'sports_soccer',
                pageTitle: 'game.title',
                pageSubtitle: 'game.subtitle'
              }
            },
            {
              path: '/team/:id',
              component: () => import('./views/team/TeamHeader'),
              children: [
                {
                  path: '',
                  name: 'team',
                  component: () => import('./views/team/TeamOverview'),
                  meta: {
                    authorize: 'ROLE_USER',
                    pageIcon: 'shield',
                    pageTitle: 'team.title',
                    pageSubtitle: 'team.subtitle'
                  }
                },
                {
                  path: '/team/:id/overview',
                  name: 'team-overview',
                  component: () => import('./views/team/TeamOverview'),
                  meta: {
                    authorize: 'ROLE_EVENTING_DATA',
                    pageIcon: 'shield',
                    pageTitle: 'team.title',
                    pageSubtitle: 'team.subtitle'
                  }
                },
                {
                  path: '/team/:id/video-highlights',
                  name: 'team-video-highlights',
                  component: () => import('./views/video-highlights/VideoHighlightsView'),
                  meta: {
                    authorize: 'ROLE_EVENTING_DATA',
                    pageIcon: 'shield',
                    pageTitle: 'team.title',
                    pageSubtitle: 'team.subtitle'
                  }
                },
                {
                  path: '/team/:id/beeswarm',
                  name: 'team-beeswarm',
                  component: () => import('./views/team/TeamBeeswarmView'),
                  meta: {
                    authorize: 'ROLE_EVENTING_DATA',
                    pageIcon: 'shield',
                    pageTitle: 'team.title',
                    pageSubtitle: 'team.subtitle'
                  }
                },
                {
                  path: '/team/:id/players',
                  name: 'team-players-info',
                  component: () => import('./views/team/TeamPlayersInfo'),
                  meta: {
                    authorize: 'ROLE_USER',
                    pageIcon: 'shield',
                    pageTitle: 'team.title',
                    pageSubtitle: 'team.subtitle'
                  }
                },
                {
                  path: '/team/:id/stats',
                  name: 'team-players',
                  component: () => import('./views/team/TeamPlayers'),
                  meta: {
                    authorize: 'ROLE_USER',
                    pageIcon: 'shield',
                    pageTitle: 'team.title',
                    pageSubtitle: 'team.subtitle'
                  }
                },
                {
                  path: '/team/:id/maps',
                  name: 'team-maps',
                  component: () => import('./views/team/TeamMaps'),
                  meta: {
                    authorize: 'ROLE_EVENTING_DATA',
                    pageIcon: 'shield',
                    pageTitle: 'team.title',
                    pageSubtitle: 'team.subtitle'
                  }
                },
                {
                  path: '/team/:id/season/:seasonId/arrigo',
                  name: 'team-arrigo-maps',
                  component: () => import('./views/team/TeamArrigoMaps.vue'),
                  meta: {
                    authorize: 'ROLE_ARRIGO',
                    pageIcon: 'shield',
                    pageTitle: 'team.title',
                    pageSubtitle: 'team.subtitle'
                  }
                },
                {
                  path: '/team/:id/all-games',
                  name: 'team-latest-games',
                  component: () => import('./views/team/TeamLatestGames'),
                  meta: {
                    authorize: 'ROLE_USER',
                    pageIcon: 'shield',
                    pageTitle: 'team.title',
                    pageSubtitle: 'team.subtitle'
                  }
                },
                {
                  path: '/team/:id/analysis',
                  name: 'team-analysis',
                  component: () => import('./views/team/TeamAnalysis'),
                  meta: {
                    authorize: 'ROLE_EVENTING_DATA',
                    pageIcon: 'shield',
                    pageTitle: 'team.title',
                    pageSubtitle: 'team.subtitle'
                  }
                },
                {
                  path: '/team/:id/scouting-analysis',
                  name: 'team-documents',
                  component: () => import('./views/scouting-analysis/TeamScoutingAnalysisView'),
                  meta: {
                    authorize: ['ROLE_DRIBLAB_HUB', 'ROLE_PHYSICAL_DATA', 'ROLE_EVENTING_DATA'],
                    pageIcon: 'shield',
                    pageTitle: 'team.title',
                    pageSubtitle: 'team.subtitle'
                  }
                }
              ],
              meta: {
                authorize: 'ROLE_USER',
                pageIcon: 'shield',
                pageTitle: 'team.title',
                pageSubtitle: 'team.subtitle'
              }
            },
            {
              path: '/leagues',
              name: "leagues",
              component: () => import('./views/league/LeaguesView'),
              meta: {
                authorize: 'ROLE_USER',
                pageIcon: 'emoji_events',
                pageTitle: 'table.league.title',
                pageSubtitle: 'table.league.subtitle'
              }
            },
            {
              path: '/league/:id',
              component: () => import('./views/league/LeagueHeader'),
              meta: {
                authorize: 'ROLE_USER',
                pageIcon: 'emoji_events',
                pageTitle: 'table.league.title',
                pageSubtitle: 'table.league.subtitle'
              }
            },
            {
              path: '/global/leagues/elos',
              name: 'global-leagues-elos',
              component: () => import('./views/global/LeagueElosView'),
              meta: {
                authorize: 'ROLE_EVENTING_DATA',
                pageIcon: 'emoji_events',
                pageTitle: 'global.leagueElos.title',
                pageSubtitle: 'global.leagueElos.subtitle'
              }
            },
            {
              path: '/global/teams/elos',
              name: 'global-teams-elos',
              component: () => import('./views/global/TeamElosView'),
              meta: {
                authorize: 'ROLE_EVENTING_DATA',
                pageIcon: 'shield',
                pageTitle: 'global.teamElos.title',
                pageSubtitle: 'global.teamElos.subtitle'
              }
            },
            {
              path: '/global/players',
              name: 'global-players',
              component: () => import('./views/global/GlobalPlayersView'),
              meta: {
                authorize: 'ROLE_EVENTING_DATA',
                pageIcon: 'directions_run',
                pageTitle: 'global.players.title',
                pageSubtitle: 'global.players.subtitle'
              }
            },
            {
              path: '/squad-builder',
              name: 'squad-builder',
              component: () => import('./views/squad-builder/SquadBuilderView'),
              meta: {
                authorize: 'ROLE_EVENTING_DATA',
                pageIcon: 'extension',
                pageTitle: 'app.squadBuilder',
                pageSubtitle: 'squadBuilder.subtitle'
              }
            },
            {
              path: '/team-on-off',
              name: 'team-on-off',
              component: () => import('./views/team/TeamOnOff'),
              meta: {
                authorize: 'ROLE_EVENTING_DATA',
                pageIcon: 'power_settings_new',
                pageTitle: 'team.onOff',
                pageSubtitle: 'team.onOffTab.subtitle'
              }
            },
            {
              path: '/squad-builder/:id',
              name: 'squad-builder-detail',
              component: () => import('./views/squad-builder/SquadBuilderView'),
              meta: {
                authorize: 'ROLE_EVENTING_DATA',
                pageIcon: 'extension',
                pageTitle: 'app.squadBuilder',
                pageSubtitle: 'squadBuilder.subtitle'
              }
            },
            {
              path: '/team-stats',
              name: 'team-stats',
              component: () => import('./views/team/TeamStats'),
              meta: {
                authorize: "ROLE_EVENTING_DATA",
                pageIcon: 'scatter_plot',
                pageTitle: 'app.teamStats',
                pageSubtitle: 'teamStats.subtitle'
              }
            },
            {
              path: '/faq',
              name: 'faq',
              component: () => import('./views/faq/Faq'),
              meta: {
                authorize: "ROLE_USER",
                pageIcon: 'live_help',
                pageTitle: 'general.faq',
                pageSubtitle: 'faq.subtitle'
              }
            },
            {
              path: '/api',
              name: 'api',
              component: () => import('./views/faq/FaqApi'),
              meta: {
                authorize: "ROLE_API",
                pageIcon: 'account_tree',
                pageTitle: 'faq.categories.api',
                pageSubtitle: 'faq.api.subtitle'
              }
            },
            {
              path: '/blog',
              name: 'blog',
              component: () => import('./views/Blog'),
              meta: {
                authorize: "ROLE_USER",
                pageIcon: 'menu_book',
                pageTitle: 'app.blog',
                pageSubtitle: 'blog.subtitle'
              }
            },
            {
              path: '/request-report',
              name: 'request-report',
              component: () => import('./views/RequestReport'),
              meta: {
                authorize: "ROLE_REPORTS",
                pageIcon: 'assignment',
                pageTitle: 'playerComparison.requestReport',
                pageSubtitle: 'requestReport.subtitle'
              }
            },
            {
              path: '/library',
              name: 'library',
              component: () => import('./views/library/LibraryHub'),
              meta: {
                authorize: ['ROLE_DRIBLAB_HUB', 'ROLE_PHYSICAL_DATA', 'ROLE_EVENTING_DATA'],
                pageIcon: 'library_books',
                pageTitle: 'general.library',
                pageSubtitle: 'library.subtitle'
              }
            },
            {
              path: '/scouting-analyses',
              name: 'ScoutingAnalyses',
              component: () => import('./views/library/ScoutingAnalysesPage'),
              meta: {
                authorize: 'ROLE_DRIBLAB_HUB',
                pageIcon: 'flare',
                pageTitle: 'scoutingAnalysis.scoutingAnalyses',
                pageSubtitle: 'library.subtitle'
              }
            },
            {
              path: '/unauthorized',
              name: "unauthorized",
              component: () => import('./views/Unauthorized.vue'),
              meta: {
                authorize: "ROLE_USER",
                pageIcon: 'vpn_key',
                pageTitle: 'Unauthorized',
                pageSubtitle: 'Please, go back to home page'

              }
            },
            {
              path: '*',
              component: () => import('./views/NotFound.vue'),
              meta: {
                authorize: "ROLE_USER",
                pageIcon: 'local_cafe',
                pageTitle: 'Page not found',
                pageSubtitle: 'Please, go back to home page'
              }
            }
          ]
        }
      ]
    },
    {
      path: '/not-found',
      name: 'not-found',
      component: () => import('./views/NotFound.vue'),
      meta: {
        authorize: "ROLE_USER",
        pageIcon: 'local_cafe',
        pageTitle: 'Page not found',
        pageSubtitle: 'Please, go back to home page'
      }
    },
    {
      path: '*',
      component: () => import('./views/NotFound.vue'),
      meta: {
        authorize: "ROLE_USER",
        pageIcon: 'local_cafe',
        pageTitle: 'Page not found',
        pageSubtitle: 'Please, go back to home page'
      }
    }
  ],
});

export default new Router();

router.onError(error =>{
    if (/loading chunk \d* failed./i.test(error.message) && navigator.onLine) {
      window.location.reload()
    }
});

router.beforeEach(async (to, from, next) => {

  const publicPages = ['/login', '/register', '/reset-password','/embed-chart','/ext','/vision'];
  const authRequired = !_.some(publicPages,p => _.includes(to.path,p));
  const loggedIn = localStorage.getItem('user') && (JSON.parse(localStorage.getItem('user')).numLogins || JSON.parse(localStorage.getItem('user')).impostor);
  const notAllowedComponentsByGameSatus = ['game-stats', 'game-pass-map', 'game-flow', 'game-threat', 'post-game-analysis', 'game-possessions'];
  // authorized if ROLE_ADMIN or if contains that roles
  const authorized = isAuhtorized(to.meta.authorize) || !authRequired;
  // trying to access a restricted page + not logged in
  const splitUrl = to.path.split('/').filter(p => p);
  let category = splitUrl[0] ? splitUrl[0] : 0;
  let urlId = splitUrl[1] ? splitUrl[1] : '';
  let tool = splitUrl[2] ? splitUrl[2] : '';

  if (category === 'global') {
    const subSection = splitUrl[1] || '';
    if (['leagues', 'teams', 'players'].includes(subSection)) {
      category = subSection;
      tool = 'global';
      urlId = 0;
    }
  }


  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const browser = navigator.userAgent;

  // Navigation analytics (fac_user_search via POST /api/user/search). Excludes /reset-password/* so we never send
  // a bogus SearchRequest (e.g. category "reset-password", empty tool, long token in URL). Reset audit is server-side on PUT /api/auth/update-password/{uuid}.
  if(loggedIn && !to.path.includes("/player-comparison") && !to.path.includes("/embed-chart") && !to.path.includes("/ext")
      && !to.path.includes("/performance") && !(splitUrl[0] === "player" && splitUrl.length == 2)
      && !to.path.includes("/overview") && !(splitUrl[0] === "team" && splitUrl.length == 2)
      && !to.path.startsWith("/reset-password") && !to.path.startsWith("/vision")
    ){
      // If urlId is not a number (e.g. 'game'), try to take numeric id from tool, otherwise fall back to 0.
      if (isNaN(urlId)) {
        if (tool && !isNaN(tool)) {
          urlId = tool;
          tool = '';
        } else {
          urlId = 0;
        }
      }

      // If urlId is a comma-separated list, take the last part
      if (typeof urlId === 'string' && urlId.includes(",")) {
        urlId = _.last(urlId.split(","));
      }

      // Ensure urlId is numeric before sending to backend (SearchRequest.url_id is long)
      urlId = Number(urlId) || 0;

      UserService.putLastSearchOut();
      UserService.postUserSearch(category, tool, urlId, authorized,
        (to.path === '/unauthorized' || to.path === '/not-found') ? from.path : to.path, timeZone, browser).then(searchResponse => {
          store.dispatch('auth/setLastSearch', searchResponse.data);
      }).catch(() => {
        // Non-blocking: failed navigation search must not break routing
      });
  }
  //Don't allow redirect that components with game status less than 8
  if(notAllowedComponentsByGameSatus.includes(to.name)){
    let gameResponse = to.params.id ? await GameService.getGame(to.params.id) : null;
    if(gameResponse && gameResponse.data.statusId <= 8){
      //redirect to Game Details
      next('/game/' + to.params.id);
    }
  }

  //go to open pages if user has not eventing role
  if (loggedIn && to.path === `/player/${to.params.id}` && !isAuhtorized('ROLE_EVENTING_DATA')) {
    next(`/player/${to.params.id}/all-games`);
  } else if (loggedIn && to.path === `/team/${to.params.id}` && !isAuhtorized('ROLE_EVENTING_DATA')) {
    next(`/team/${to.params.id}/players`);
  };

  //save redirect path if is protected
  if(authRequired){
    localStorage.setItem('redirectPath', to.path);
  }

  // redirect to login page
  if (authRequired && !loggedIn) {
    store.dispatch('auth/logout'); // This is for the case when the user deletes the local storage
  } else if (loggedIn && publicPages.includes(to.path) && to.path != '/embed-chart' && !to.path.includes('/ext')) {
    // if already logged in and trying to go to login, go to home
    next('/home');
  } else if (loggedIn && !authorized && to.path !== '/unauthorized') {
    // if login but not authorized, send to 401
    // to path condition so it stops forwarding
    next('/unauthorized');
  } else {
    next();
  }
});


export function isAuhtorized(rolesAllowed) {
  const user = JSON.parse(localStorage.getItem('user'));
  let userHasRole;
  if(Array.isArray(rolesAllowed)){
    //rolesAllowed is an array of roles
    userHasRole = rolesAllowed.some(r => user && user.roles.includes(r));
  }else{
    //rolesAllowed is just one role (String)
    userHasRole = user && user.roles.includes(rolesAllowed);
  }

  return (user && user.roles.includes('ROLE_USER') && (user.roles.includes('ROLE_ADMIN') || userHasRole));
}



// WEBPACK FOOTER //
// ./src/router.js